import React, {useEffect} from 'react';
import {
  FlatList,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import {faChevronRight} from '@fortawesome/free-solid-svg-icons/faChevronRight';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {observer} from 'mobx-react-lite';

import LoadingImg from '../../components/LoadingImg';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {toAUDAbs} from '../../utils/functions';

function ItemSeparatorComponent() {
  return <View style={styles.separator} />;
}

const RecentOrderScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_RECENT_ORDERS>) => {
    const {cartStore, userStore} = useStores();

    useEffect(() => {
      if (userStore.sessionToken) cartStore.loadRecentOrders();
    }, [cartStore, userStore.sessionToken]);

    const onPress = (item: any) =>
      navigation.navigate(AppRoute.ACCOUNT_ORDER_VIEW, {
        order: item,
      });

    const getStatus = (item: any) => {
      if (item.cancelled) {
        return (
          <View style={styles.cancelledHolder}>
            <Text style={styles.statusText}>CANCELLED</Text>
          </View>
        );
      } else if (item.checkout?.succeeded) {
        return (
          <View style={styles.completeHolder}>
            <Text style={styles.statusText}>COMPLETE</Text>
          </View>
        );
      } else if (item.checkout?.failed || item.checkout?.in_doubt) {
        return (
          <View style={styles.failHolder}>
            <Text style={styles.statusText}>PROBLEM</Text>
          </View>
        );
      } else if (!item.online_payments?.succeeded) {
        return (
          <View style={styles.cancelledHolder}>
            <Text style={styles.statusText}>PAYMENT ISSUE</Text>
          </View>
        );
      } else {
        return <View />;
      }
    };

    const renderItem = ({item}: {item: any}) => (
      <TouchableOpacity onPress={() => onPress(item)} style={styles.item}>
        <View style={styles.itemInside}>
          <Text style={styles.itemText}>Order #{item.order_number}</Text>
          <Text style={[styles.itemText, styles.subtle]}>
            {item.time_slot?.delivery_zone?.name || ''}
          </Text>
          <View>
            <Text style={styles.itemText}>
              {toAUDAbs(item.total.cents_inc_tax, true)}
            </Text>
            {getStatus(item)}
          </View>
        </View>
        <FontAwesomeIcon icon={faChevronRight} size={12} style={styles.icon} />
      </TouchableOpacity>
    );

    const renderItemSeparatorComponent = () => (
      <ItemSeparatorComponent style={styles.separator} />
    );

    return (
      <ContentView>
        <FlatList
          data={cartStore.recentOrders}
          renderItem={renderItem}
          style={styles.flatList}
          contentContainerStyle={styles.contentContainer}
          ItemSeparatorComponent={renderItemSeparatorComponent}
          ListEmptyComponent={() =>
            cartStore.loadingRecentOrders ? (
              <LoadingImg />
            ) : (
              <View
                style={{
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  height: 200,
                }}>
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: '500',
                    color: theme.colour.text.heading.primary,
                  }}>
                  NO RECENT ORDERS FOUND
                </Text>
              </View>
            )
          }
        />
      </ContentView>
    );
  },
);

export default RecentOrderScreen;

const styles = StyleSheet.create({
  item: {
    paddingVertical: 16,
    marginHorizontal: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemInside: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  itemText: {
    fontSize: 16,
    color: 'black',
    flex: 1,
    textAlign: 'center',
  },
  subtle: {
    color: 'grey',
  },
  flatList: {
    flex: 1,
  },
  contentContainer: {
    ...Platform.select({
      ios: {},
      web: {
        flex: 1,
      },
    }),
  },
  icon: {
    color: 'grey',
  },
  separator: {
    borderBottomWidth: 1,
    borderBottomColor: 'lightgrey',
    marginHorizontal: 8,
  },
  activity: {
    justifyContent: 'center',
  },
  cancelledHolder: {
    backgroundColor: 'orange',
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 4,
    flex: 1,
  },
  completeHolder: {
    backgroundColor: theme.colour.background.general.primary,
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 4,
    flex: 1,
  },
  failHolder: {
    backgroundColor: 'red',
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 4,
    flex: 1,
  },
  statusText: {
    color: 'white',
  },
  cancelText: {
    color: 'white',
    fontSize: 12,
  },
});
